import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Avatar, ChatMessage, Participant } from "@kinop/shared";
import { LIMITS, parseSpoilers, wrapSpoiler } from "@kinop/shared";
import { Button } from "@/components/ui/button";
import { UserAvatar, UserBadge } from "./UserAvatar.js";
import { StickerImage, StickerPicker } from "./StickerPicker.js";
import { StickerPackSheet } from "./StickerPackSheet.js";
import { EmojiPicker } from "./EmojiPicker.js";

interface ChatProps {
  messages: ChatMessage[];
  participants: Participant[];
  myClientId: string | null;
  onSend: (text: string) => void;
  /** Отправка стикера из телеграма. */
  onSendSticker: (stickerId: string) => void;
}

/**
 * Время сообщения.
 *
 * Формат прибит к ru-RU вместе с языком интерфейса: смешивать русские
 * подписи с «2:05 PM» — значит собрать ленту из двух разных приложений.
 * Когда появится перевод, отсюда и начнём.
 */
const timeFormat = new Intl.DateTimeFormat("ru-RU", {
  hour: "2-digit",
  minute: "2-digit",
});

/** Один пузырь сообщения: разбирает спойлеры и открывает их по нажатию. */
function MessageBody({ text }: { text: string }): JSX.Element {
  const parts = useMemo(() => parseSpoilers(text), [text]);
  const [revealed, setRevealed] = useState<Set<number>>(() => new Set());

  return (
    <p className="chat__text">
      {parts.map((part, index) =>
        part.kind === "text" ? (
          <span key={index}>{part.text}</span>
        ) : (
          <button
            key={index}
            type="button"
            className="spoiler"
            data-open={revealed.has(index) ? "" : undefined}
            aria-expanded={revealed.has(index)}
            aria-label={
              revealed.has(index) ? "Спойлер открыт" : "Показать спойлер"
            }
            onClick={() =>
              setRevealed((current) => new Set(current).add(index))
            }
          >
            {part.text}
          </button>
        ),
      )}
    </p>
  );
}

export function Chat({
  messages,
  participants,
  myClientId,
  onSend,
  onSendSticker,
}: ChatProps): JSX.Element {
  const [draft, setDraft] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  /**
   * Раздел панели. В телеграме стикеры и премиум-эмодзи живут врозь, и
   * смешивать их нельзя: эмодзи мельче, их больше, и ищут их отдельно.
   */
  const [panel, setPanel] = useState<"emoji" | "stickers" | "premium">("emoji");
  /** Открытый набор: сюда попадают, нажав на стикер в переписке. */
  const [openedPack, setOpenedPack] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
  const readingAnchor = useRef<{ key: string; offset: number } | null>(null);

  // Аватарку автора берём из списка участников: в истории её нет, а
  // хранить копию в каждом сообщении значило бы держать в базе то, что
  // человек может сменить в любой момент.
  const avatars = useMemo(() => {
    const map = new Map<string, Avatar | null>();
    for (const participant of participants)
      map.set(participant.clientId, participant.avatar);
    return map;
  }, [participants]);

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => listRef.current,
    getItemKey: useCallback((index: number) => messages[index]!.id, [messages]),
    estimateSize: (index) => messages[index]?.sticker ? 190 : messages[index]?.kind === 'system' ? 40 : 85,
    overscan: 4,
  });
  const totalHeight = virtualizer.getTotalSize();
  const remaining = LIMITS.chatMessageMaxLength - draft.length;

  // Автопрокрутка только когда человек и так внизу: иначе чтение
  // старых сообщений прерывалось бы каждым новым.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (isPinnedToBottom) {
      list.scrollTop = list.scrollHeight;
      return;
    }
    // The bounded history drops old rows. Keep the same message under the
    // reader's eyes instead of keeping an obsolete numerical scroll offset.
    const anchor = readingAnchor.current;
    if (anchor) {
      const index = messages.findIndex(message => message.id === anchor.key);
      const target = virtualizer.getOffsetForIndex(Math.max(0, index), 'start');
      if (target) list.scrollTop = target[0] + (index < 0 ? 0 : anchor.offset);
    }
  }, [messages, isPinnedToBottom, totalHeight]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const handleScroll = (): void => {
      const distanceFromBottom =
        list.scrollHeight - list.scrollTop - list.clientHeight;
      const first = virtualizer.getVirtualItems().find(item => item.end > list.scrollTop);
      readingAnchor.current = distanceFromBottom < 80 || !first ? null : {
        key: String(first.key), offset: list.scrollTop - first.start,
      };
      setIsPinnedToBottom(distanceFromBottom < 80);
    };

    list.addEventListener("scroll", handleScroll, { passive: true });
    return () => list.removeEventListener("scroll", handleScroll);
  }, []);

  const submit = (): void => {
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft("");
    setIsPinnedToBottom(true);
  };

  /** Вставка в позицию курсора, а не в конец: панель открыта поверх ввода. */
  const insertAtCaret = (insert: string): void => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? draft.length;
    const end = input?.selectionEnd ?? start;
    const next = draft.slice(0, start) + insert + draft.slice(end);
    setDraft(next.slice(0, LIMITS.chatMessageMaxLength));
    requestAnimationFrame(() => {
      input?.focus();
      const caret = start + insert.length;
      input?.setSelectionRange(caret, caret);
    });
  };

  const hideSelection = (): void => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? draft.length;
    const end = input?.selectionEnd ?? start;
    const result = wrapSpoiler(draft, start, end);
    setDraft(result.value.slice(0, LIMITS.chatMessageMaxLength));
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(result.caret, result.caret);
    });
  };

  return (
    <section className="chat" aria-label="Чат комнаты">
      {/* Живая область: новые сообщения зачитываются экранным диктором.
          additions — чтобы при входе в комнату не прочитали всю переписку. */}
      <div
        className="chat__messages"
        ref={listRef}
        role="log"
        aria-label="Сообщения"
        aria-live="off"
      >
        {messages.length === 0 && (
          <p className="chat__empty">Пока тихо. Напишите что-нибудь!</p>
        )}

        <div style={{ height: totalHeight, width: '100%', position: 'relative', flexShrink: 0 }}>
          {virtualizer.getVirtualItems().map(item => {
            const message = messages[item.index]!;
            return <div key={item.key} data-index={item.index} ref={virtualizer.measureElement}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', paddingBottom: 12, transform: `translateY(${item.start}px)` }}>
              <ChatRow message={message} avatar={message.authorClientId ? avatars.get(message.authorClientId) ?? null : null}
                myClientId={myClientId} onOpenPack={setOpenedPack} />
            </div>;
          })}
        </div>
      </div>

      <span className="sr-only" aria-live="polite">{messages.at(-1)?.authorNickname}: {messages.at(-1)?.text}</span>
      {!isPinnedToBottom && (
        <button
          type="button"
          className="chat__scroll-down"
          onClick={() => {
            const list = listRef.current;
            if (list) list.scrollTop = list.scrollHeight;
            setIsPinnedToBottom(true);
          }}
        >
          ↓ Новые сообщения
        </button>
      )}

      {openedPack && (
        <StickerPackSheet
          setName={openedPack}
          onClose={() => setOpenedPack(null)}
          onPick={(sticker) => {
            onSendSticker(sticker.id);
            setIsPinnedToBottom(true);
          }}
        />
      )}

      {emojiOpen && (
        <div className="emoji-picker">
          {/* Значки и стикеры живут в одной панели: места под кадром мало,
              а нужны они в один и тот же момент. */}
          <div
            className="emoji-picker__modes"
            role="tablist"
            aria-label="Что вставить"
          >
            <button
              type="button"
              role="tab"
              aria-selected={panel === "emoji"}
              className="pill"
              data-on={panel === "emoji" ? "" : undefined}
              onClick={() => setPanel("emoji")}
            >
              Значки
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={panel === "stickers"}
              className="pill"
              data-on={panel === "stickers" ? "" : undefined}
              onClick={() => setPanel("stickers")}
            >
              Стикеры
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={panel === "premium"}
              className="pill"
              data-on={panel === "premium" ? "" : undefined}
              onClick={() => setPanel("premium")}
            >
              Эмодзи ТГ
            </button>
          </div>

          {panel === "stickers" || panel === "premium" ? (
            <StickerPicker
              /* key заставляет собрать панель заново при смене раздела:
                 иначе от прошлого остаются его наборы и открытая вкладка. */
              key={panel}
              kind={panel === "premium" ? "custom_emoji" : "regular"}
              onPick={(sticker) => {
                onSendSticker(sticker.id);
                setEmojiOpen(false);
                setIsPinnedToBottom(true);
              }}
            />
          ) : (
            <>
              <EmojiPicker onPick={insertAtCaret} />
            </>
          )}
        </div>
      )}

      <form
        className="chat__form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <button
          type="button"
          className="chat__icon-btn"
          data-on={emojiOpen ? "" : undefined}
          aria-expanded={emojiOpen}
          aria-label="Эмодзи"
          onClick={() => setEmojiOpen((open) => !open)}
        >
          ☺
        </button>

        <textarea
          ref={inputRef}
          className="chat__input"
          value={draft}
          onChange={(event) =>
            setDraft(event.target.value.slice(0, LIMITS.chatMessageMaxLength))
          }
          onKeyDown={(event) => {
            // Enter отправляет, Shift+Enter переносит строку.
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder="Написать…"
          rows={1}
          maxLength={LIMITS.chatMessageMaxLength}
          aria-label="Текст сообщения"
        />

        <button
          type="button"
          className="chat__icon-btn"
          aria-label="Спрятать выделенное под спойлер"
          title="Выдели текст и нажми — уйдёт под спойлер ||так||"
          onClick={hideSelection}
        >
          ▚
        </button>

        {/* Счётчик появляется только у предела: иначе он висел бы на экране
            всё время и мешал бы читать переписку. */}
        {remaining <= 40 && (
          <span className="chat__counter mono" aria-live="polite">
            {remaining}
          </span>
        )}

        <Button
          type="submit"
          size="sm"
          disabled={draft.trim().length === 0}
          aria-label="Отправить"
        >
          →
        </Button>
      </form>
    </section>
  );
}

const ChatRow = memo(function ChatRow({ message, avatar, myClientId, onOpenPack }: {
  message: ChatMessage; avatar: Avatar | null; myClientId: string | null;
  onOpenPack: (name: string | null) => void;
}): JSX.Element {
  return (          message.kind === "system" ? (
            <div  className="chat__system">
              {message.text}
            </div>
          ) : (
            <div
              
              className={`chat__message ${
                message.authorClientId === myClientId
                  ? "chat__message--own"
                  : ""
              }`}
            >
              <UserAvatar
                nickname={message.authorNickname ?? "Гость"}
                avatar={
                  message.authorClientId
                    ? (avatar ?? null)
                    : null
                }
                className="avatar--sm"
              />
              <div className="chat__body">
                <div className="chat__meta mono">
                  <span className="chat__author">
                    {message.authorNickname ?? "Гость"}
                    <UserBadge
                      badge={
                        message.authorClientId
                          ? avatar?.badge
                          : null
                      }
                    />
                  </span>
                  <span aria-hidden="true"> · </span>
                  <time dateTime={new Date(message.createdAt).toISOString()}>
                    {timeFormat.format(new Date(message.createdAt))}
                  </time>
                </div>
                {message.sticker ? (
                  <button
                    type="button"
                    className="chat__sticker"
                    title="Открыть набор"
                    aria-label={`Открыть набор стикера ${message.sticker.emoji ?? ''}`.trim()}
                    onClick={() => onOpenPack(message.sticker?.setName ?? null)}
                  >
                    <StickerImage sticker={message.sticker} className="sticker--message" />
                  </button>
                ) : (
                  <MessageBody text={message.text} />
                )}
              </div>
            </div>
          ));
});
